import React, { useState } from "react";
import ConfirmationModal from "./ConfirmationModal";
import { TrashIcon } from '@heroicons/react/24/outline';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const DeletedUserList = ({ deletedUsers, fetchUsers, setDeletedUsers }) => {
  const [isRestoreModalOpen, setRestoreModalOpen] = useState(false);
  const [isDeleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState(""); // State for search input

  const openRestoreModal = (userId) => {
    setSelectedUserId(userId);
    setRestoreModalOpen(true);
  };

  const openDeleteModal = (userId) => {
    setSelectedUserId(userId);
    setDeleteModalOpen(true);
  };

  const confirmRestoreUser = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`/api/users/restore/${selectedUserId}`, {
        method: "PATCH",
      });
      if (!response.ok) throw new Error(`Error: ${response.status} - ${response.statusText}`);
      fetchUsers();
      setDeletedUsers((prev) => prev.filter((user) => user._id !== selectedUserId));
      toast.success("User restored successfully.");
    } catch (error) {
      console.error("Error restoring user:", error);
      toast.error("Failed to restore the user. Please try again.");
    } finally {
      setRestoreModalOpen(false);
      setIsLoading(false);
    }
  };

  const confirmPermanentDeleteUser = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`/api/deleted-users/${selectedUserId}/permanent`, {
        method: "DELETE",
      });
      if (!response.ok) throw new Error(`Error: ${response.status} - ${response.statusText}`);
      fetchUsers();
      setDeletedUsers((prev) => prev.filter((user) => user._id !== selectedUserId));
      toast.success("User permanently deleted successfully.");
    } catch (error) {
      console.error("Error permanently deleting user:", error);
      toast.error("Failed to permanently delete the user. Please try again.");
    } finally {
      setDeleteModalOpen(false);
      setIsLoading(false);
    }
  };

// Filter users based on search term
const filteredUsers = deletedUsers.filter(user =>
  (user.firstname && user.firstname.toLowerCase().includes(searchTerm.toLowerCase())) ||
  (user.lastname && user.lastname.toLowerCase().includes(searchTerm.toLowerCase())) ||
  (user.username && user.username.toLowerCase().includes(searchTerm.toLowerCase()))
);

  return (
    <div className="bg-white p-6 rounded-lg shadow-lg mb-6">
      <h2 className="text-2xl font-bold text-gray-800 mb-4">Deleted Users</h2>
      
      {/* Search Input */}
      <input
        type="text"
        placeholder="Search deleted users..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        className="border border-gray-300 p-2 mb-4 rounded"
      />
      
      <div className="overflow-x-auto">
        <table className="w-full text-left border-collapse">
          <thead className="bg-gray-200 text-gray-600">
            <tr>
              <th className="py-3 px-5 border-b border-gray-300">Name</th>
              <th className="py-3 px-5 border-b border-gray-300">Email</th>
              <th className="py-3 px-5 border-b border-gray-300">Role</th>
              <th className="py-3 px-5 border-b border-gray-300">Status</th>
              <th className="py-3 px-5 border-b border-gray-300">Action</th>
            </tr>
          </thead>
          <tbody>
            {filteredUsers.length === 0 ? (
              <tr>
                <td colSpan={5} className="py-3 px-5 border-b border-gray-300 text-center text-gray-500">
                  No deleted users found.
                </td>
              </tr>
            ) : (
              filteredUsers.map((user) => (
                <tr key={user._id} className="hover:bg-gray-50 transition duration-200">
                  <td className="py-3 px-5 border-b border-gray-300">{user.firstname} {user.lastname}</td>
                  <td className="py-3 px-5 border-b border-gray-300">{user.username}</td>
                  <td className="py-3 px-5 border-b border-gray-300">{user.role}</td>
                  <td className="py-3 px-5 border-b border-gray-300">
                    {user.isPermanent ? "Permanent" : "Temporary"}
                  </td>
                  <td className="py-3 px-5 border-b border-gray-300 flex space-x-2">
                    <button
                      onClick={() => openRestoreModal(user._id)}
                      className={`bg-blue-600 text-white py-1 px-3 rounded-sm hover:bg-blue-700 transition duration-200 ${isLoading ? "opacity-50 cursor-not-allowed" : ""}`}
                      disabled={isLoading}
                    >
                      Restore
                    </button>
                    <button
                      onClick={() => openDeleteModal(user._id)}
                      className={`bg-red-600 text-white p-2 rounded-md hover:bg-red-700 transition duration-200 ${isLoading ? "opacity-50 cursor-not-allowed" : ""}`}
                      aria-label="Permanent Delete"
                      disabled={isLoading}
                    >
                      <TrashIcon className="h-5 w-5" />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Restore Confirmation Modal */}
      <ConfirmationModal
        isOpen={isRestoreModalOpen}
        onClose={() => setRestoreModalOpen(false)}
        onConfirm={confirmRestoreUser}
        message="Are you sure you want to restore this user?"
      />

      {/* Permanent Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onConfirm={confirmPermanentDeleteUser}
        message="Are you sure you want to permanently delete this user? This action cannot be undone."
      />

      {/* Notification Container */}
      <ToastContainer />
    </div>
  );
};

export default DeletedUserList;
